/**
 * ThriveSearchStep — the SOURCE search of a Thrive pull (epic
 * 2026-10-15-thrive-search-import, sub-task 03).
 *
 * The same `SearchTypeahead` as Link to Search, reading EVERY status, with a
 * status pill on every row. The recruiter searches by name or Thrive ID; there
 * is no status filter (Jared's feedback, 2026-10-20 — it was Closed first, then
 * Open, BD, Lost and All). Once a search is picked, a summary line under it
 * says what it is, and the list title is prefilled from its name (editable).
 *
 * Tokenized: token color utilities + `text-*` roles. No inline styles.
 */

import type { SearchStatusFilter } from "../../../../clients/imports.client";
import { searchStatusClasses } from "../../../../components/searches/searchStatus";
import { formatDate } from "../../../../lib/format/date";
import { THRIVE_IMPORT_TITLE_MAX } from "../useThriveImport";
import {
  SearchTypeahead,
  type SearchTypeaheadOption,
  type SearchTypeaheadResult,
} from "./SearchTypeahead";

/** The source picker reads every status, with no filter control (one entry). */
export const SOURCE_SEARCH_STATUSES: readonly SearchStatusFilter[] = ["all"];

export interface ThriveSearchStepProps {
  value: SearchTypeaheadOption | null;
  onChange: (option: SearchTypeaheadOption | null) => void;
  search: (query: string, status?: SearchStatusFilter) => Promise<SearchTypeaheadResult>;
  title: string;
  onTitleChange: (title: string) => void;
}

/** `Mar 2, 2026 – Sep 30, 2026`, `Opened Mar 2, 2026`, `Closed Sep 30, 2026`;
 *  `null` when neither date is known. */
export function searchDates(option: SearchTypeaheadOption): string | null {
  const opened = option.thriveCreatedAt ? formatDate(option.thriveCreatedAt) : null;
  const closed = option.closedAt ? formatDate(option.closedAt) : null;
  if (opened && closed) return `${opened} – ${closed}`;
  if (opened) return `Opened ${opened}`;
  if (closed) return `Closed ${closed}`;
  return null;
}

export function ThriveSearchStep({
  value,
  onChange,
  search,
  title,
  onTitleChange,
}: ThriveSearchStepProps) {
  const dates = value ? searchDates(value) : null;
  const count = value?.candidaciesCount;
  return (
    <div data-testid="thrive-search-step" className="flex flex-col gap-3">
      <label htmlFor="thrive-source-search" className="text-label text-text">
        Thrive search to pull from <span className="text-warning">*</span>
      </label>
      <div id="thrive-source-search">
        <SearchTypeahead
          value={value?.id ?? null}
          valueName={value?.name ?? null}
          onPick={onChange}
          onChange={(id) => {
            // Change clears; a pick already arrived whole through `onPick`.
            if (id === null) onChange(null);
          }}
          search={search}
          statuses={SOURCE_SEARCH_STATUSES}
          autoFocus
          openOnFocus
        />
      </div>
      {value ? (
        <p
          data-testid="thrive-source-summary"
          className="flex flex-wrap items-center gap-x-2 gap-y-1 text-caption text-text-secondary"
        >
          {value.status ? (
            <span className={searchStatusClasses(value.status)}>{value.status}</span>
          ) : null}
          {value.company ? <span>{value.company}</span> : null}
          {count != null ? (
            <span data-testid="thrive-source-count">
              {count.toLocaleString()} {count === 1 ? "candidate" : "candidates"} in Thrive
            </span>
          ) : null}
          {dates ? <span>{dates}</span> : null}
        </p>
      ) : (
        <p className="text-caption text-text-secondary">
          Search by name or Thrive ID, at any status.
        </p>
      )}
      {value ? (
        <div className="flex flex-col gap-1">
          <label htmlFor="thrive-import-title" className="text-label text-text">
            Name for list you’re about to create
          </label>
          <input
            id="thrive-import-title"
            data-testid="thrive-import-title"
            type="text"
            value={title}
            maxLength={THRIVE_IMPORT_TITLE_MAX}
            onChange={(e) => onTitleChange(e.target.value)}
            className="w-full rounded-md border border-border bg-bg px-3 py-1.5 text-body text-text placeholder:text-text-muted focus:border-accent focus:outline-none"
          />
        </div>
      ) : null}
    </div>
  );
}
